<script setup lang="ts">
import type { AuthGatedAction } from '~/components/Auth/AuthGatedActionInfo.vue';

export interface AuthModalProps {
    email?: string;
    phone?: string;
    action?: AuthGatedAction;
}

type AuthStep = 'Login' | 'Signup' | 'Verify' | 'Onboarding';

// PROPS
const { email: emailProp, phone } = defineProps<AuthModalProps>();

// EVENTS
const emit = defineEmits<{
    close: [boolean];
}>();

// COMPOSABLES
const { isOnboardingInProgress } = useOnboarding();
const { isMobile } = useResponsive();
const { getSession } = useAuthUtils();
const { tt } = useTypedI18n();

// DATA
const requestOtp = ref<boolean>(false);
const email = ref<string | undefined>(emailProp);
const step = ref<AuthStep>('Login');
const title = computed(() => {
    if (step.value === 'Login') return tt('login.title');

    if (step.value === 'Signup') return tt('signup.title');

    if (step.value === 'Verify') return tt('signup.verify.title');

    if (step.value === 'Onboarding') return tt('signup.onboarding.title');
});
const closeEnabled = computed(() => ['Login', 'Signup'].includes(step.value));

// HANDLERS
const onLogin = (): void => {
    if (isOnboardingInProgress.value) {
        step.value = 'Onboarding';

        return;
    }

    emit('close', true);
};

const onEmailChange = async (): Promise<void> => {
    email.value = undefined;
    step.value = 'Login';
};

const onNotVerified = async (verifyEmail: string): Promise<void> => {
    email.value = verifyEmail;
    requestOtp.value = true;
    step.value = 'Verify';
};

const onNotFound = (signupEmail: string): void => {
    email.value = signupEmail;
    step.value = 'Signup';
};

const goTo = (nextStep: AuthStep): void => {
    step.value = nextStep;
};

const onOnboardingSuccess = async (): Promise<void> => {
    await getSession();
    emit('close', true);
};

// LIFECYCLE
onMounted(() => {
    if (isOnboardingInProgress.value) step.value = 'Onboarding';
});
</script>

<template>
    <UModal :fullscreen="isMobile" :title="title" :transition="!isMobile" :close="closeEnabled" v-bind="modalNoDescAriaFixupProps">
        <slot />
        <template #body>
            <AuthGatedActionInfo class="mb-4" :action="action" />

            <Transition
                enter-from-class="opacity-0"
                leave-to-class="opacity-0"
                enter-active-class="transition-opacity duration-300"
                leave-active-class="transition-opacity duration-[0]"
                mode="out-in"
            >
                <LoginForm v-if="step === 'Login'" :email="email" @success="onLogin" @not-verified="onNotVerified" @not-found="onNotFound" />
                <SignupForm
                    v-else-if="step === 'Signup' && email"
                    :email="email"
                    :phone="phone"
                    @edit-email="onEmailChange"
                    @success="goTo('Verify')"
                />
                <SignupVerifyOtpForm v-else-if="step === 'Verify' && email" :email="email" :request-otp="requestOtp" @success="goTo('Onboarding')" />
                <SignupOnboarding v-else-if="step === 'Onboarding'" :action="action" @success="onOnboardingSuccess" />
            </Transition>
        </template>
    </UModal>
</template>
